// src/app/components/flashOffer/flashOfferService.js
//
// Accès aux ventes flash de la page d'accueil.
//
// ⚠️ DONNÉES DE TEST — l'API est en cours de développement côté back.
// Quand elle sera prête, seul `fetchFlashOffers` change : il appellera l'API
// et renverra un tableau d'objets de la forme ci-dessous (tableau vide s'il
// n'y a pas de vente en cours). Le reste (hook, section, carrousel) ne bouge pas.
//
// Forme attendue de l'API (une entrée par vente) :
// {
//   id, tripId, tripDateId,
//   title, titleHighlight?,          // partie du titre mise en avant
//   shortTitle?,                     // nom court de l'onglet (« Istanbul »)
//   destination, nights, inclusions: [string],
//   picture, pictureAlt?,
//   endsAt: ISO,                     // fin de l'offre, fournie par le serveur
//   stock: { total, remaining },
//   price: { original, current, currency, basis },
//   nextDeparture: "YYYY-MM-DD",
//   guarantee?
// }

const MOCK_DELAY_MS = 400;
const HOUR = 3600 * 1000;
const DAY = 24 * HOUR;

/* Les échéances sont recalculées à chaque appel pour que les minuteurs de
   test tournent toujours. L'API, elle, renverra des dates fixes. */
const buildMocks = () => [
  {
    id: "flash-mock-marrakech",
    tripId: null,
    tripDateId: null,
    title: "Marrakech en vente privée, places limitées",
    titleHighlight: "vente privée",
    shortTitle: "Marrakech",
    destination: "Maroc, Marrakech",
    nights: 5,
    inclusions: ["Riad 4★", "Guide francophone"],
    picture:
      "https://res.cloudinary.com/hello-tickets/image/upload/c_limit,f_auto,q_auto,w_1300/v1645588616/j4ssef2gssrjvzxbbqpr.jpg",
    pictureAlt: "Marrakech, place Jemaa el-Fna au crépuscule",
    endsAt: new Date(Date.now() + 3 * DAY + 14 * HOUR + 27 * 60 * 1000).toISOString(),
    stock: { total: 46, remaining: 6 },
    price: { original: 7900, current: 5900, currency: "MAD", basis: "par personne, base ch. double" },
    nextDeparture: "2026-10-12",
    guarantee: "Annulation gratuite sous 48h · Paiement en 4× sans frais",
  },
  {
    id: "flash-mock-istanbul",
    tripId: null,
    tripDateId: null,
    title: "Istanbul, entre deux continents",
    titleHighlight: "entre deux continents",
    shortTitle: "Istanbul",
    destination: "Turquie, Istanbul",
    nights: 5,
    inclusions: ["Hôtel 4★", "Croisière sur le Bosphore"],
    picture:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/2/22/Hagia_Sophia_Mars_2013.jpg/1280px-Hagia_Sophia_Mars_2013.jpg",
    pictureAlt: "Sainte-Sophie, Istanbul",
    endsAt: new Date(Date.now() + 5 * HOUR + 12 * 60 * 1000).toISOString(),
    stock: { total: 40, remaining: 14 },
    price: { original: 9400, current: 7300, currency: "MAD", basis: "par personne, base ch. double" },
    nextDeparture: "2026-10-03",
    guarantee: "Annulation gratuite sous 48h",
  },
  {
    id: "flash-mock-omra-dubai",
    tripId: null,
    tripDateId: null,
    title: "Omra et escapade à Dubaï",
    titleHighlight: "escapade à Dubaï",
    shortTitle: "Omra et Dubaï",
    destination: "Arabie saoudite · Émirats, Dubaï",
    nights: 12,
    inclusions: ["Hôtels 5★", "Guide religieux francophone"],
    picture: "https://upload.wikimedia.org/wikipedia/commons/e/e0/Burj_dubai_3.11.08.jpg",
    pictureAlt: "Burj Khalifa, Dubaï",
    endsAt: new Date(Date.now() + 6 * DAY + 2 * HOUR).toISOString(),
    stock: { total: 40, remaining: 22 },
    price: { original: 38900, current: 31900, currency: "MAD", basis: "par personne, base ch. quadruple" },
    nextDeparture: "2026-11-16",
    guarantee: "Visa et transferts inclus",
  },
  {
    id: "flash-mock-egypte",
    tripId: null,
    tripDateId: null,
    title: "Égypte des pharaons, croisière sur le Nil",
    titleHighlight: "croisière sur le Nil",
    shortTitle: "Égypte",
    destination: "Égypte, Louxor et Assouan",
    nights: 9,
    inclusions: ["Croisière 5★", "Égyptologue francophone"],
    picture:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/a/af/All_Gizah_Pyramids.jpg/1280px-All_Gizah_Pyramids.jpg",
    pictureAlt: "Pyramides de Gizeh",
    endsAt: new Date(Date.now() + DAY + 9 * HOUR + 40 * 60 * 1000).toISOString(),
    stock: { total: 32, remaining: 9 },
    price: { original: 24500, current: 18900, currency: "MAD", basis: "par personne, base ch. double" },
    nextDeparture: "2026-11-03",
    guarantee: "Visa et excursions inclus",
  },
];

export async function fetchFlashOffers({ signal } = {}) {
  await new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, MOCK_DELAY_MS);
    signal?.addEventListener("abort", () => {
      clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    });
  });
  return buildMocks();
}

/* Traduit une vente de l'API en offre de <FlashOfferCarousel> (props de
   FlashOfferCard + id + tabLabel). Un seul endroit à ajuster si l'API
   renomme un champ. */
export const toFlashOfferProps = (offer) => ({
  id: offer.id,
  tabLabel: offer.shortTitle,
  title: offer.title,
  titleHighlight: offer.titleHighlight,
  destination: offer.destination,
  nights: offer.nights,
  inclusions: offer.inclusions,
  picture: offer.picture,
  pictureAlt: offer.pictureAlt || offer.title,
  endsAt: offer.endsAt,
  totalSeats: offer.stock?.total,
  remainingSeats: offer.stock?.remaining,
  originalPrice: offer.price?.original,
  price: offer.price?.current,
  currency: offer.price?.currency,
  priceNote: offer.price?.basis,
  nextDeparture: offer.nextDeparture,
  guarantee: offer.guarantee,
});

/* Remise la plus forte parmi les ventes, pour l'en-tête de section. */
export const getMaxDiscount = (offers) =>
  offers.reduce((max, offer) => {
    const original = Number(offer?.price?.original);
    const current = Number(offer?.price?.current);
    if (!(original > 0 && current > 0 && original > current)) return max;
    return Math.max(max, Math.round((1 - current / original) * 100));
  }, 0);
